import { useState, useEffect } from 'react';
import { Map, Play, TrendingUp, TrendingDown, Globe, Landmark, Swords, ChevronDown } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

// Sprint 22.7.12 (Tier 1 build): Scenario Engine page. Surfaces the existing
// rule-based scenarioService (GET /api/currency/scenarios + POST
// /api/currency/scenario/simulate) so the /scenarios sidebar link is live.
// Simulates a market shock against the open KAIROS shadow positions and shows
// position P&L, KDX impact, and AED purchasing-power impact.
//
// Sprint 22.7.33 (Tier 1 step 3 / M.74): LLM war-gaming layer. On-demand
// second-order analysis via POST /api/scenarios/wargame (Sonnet default,
// Fable 5 opt-in A/B). The rule-based simulator stays as the first-order
// anchor; the war-game renders consequence chains, portfolio exposure reads,
// hedges, invalidation signals, and historical analogs.

function changeLabel(changes) {
  return Object.entries(changes || {})
    .map(([k, v]) => `${k} ${v > 0 ? '+' : ''}${v}%`)
    .join(', ');
}

function fmtUsd(n) {
  if (n == null) return '—';
  const v = Math.round(n);
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString()}`;
}

export default function ScenarioEngine() {
  const { setPage } = usePage();
  useEffect(() => { setPage('scenarios'); }, []);

  const [scenarios, setScenarios] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch('/api/currency/scenarios');
        if (!r.ok) { if (alive) setErr(`HTTP ${r.status}`); return; }
        const d = await r.json();
        if (alive) setScenarios(Array.isArray(d) ? d : []);
      } catch (e) { if (alive) setErr(e.message); }
    })();
    return () => { alive = false; };
  }, []);

  async function simulate(id) {
    setSelectedId(id);
    setResult(null);
    setLoading(true);
    setErr(null);
    try {
      const r = await fetch('/api/currency/scenario/simulate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenarioId: id }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || `HTTP ${r.status}`); return; }
      setResult(d);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }

  // ─── War-game state (22.7.33) ──────────────────────────────────────────
  const [wgModel, setWgModel] = useState('sonnet');
  const [wgCustom, setWgCustom] = useState('');
  const [wgLoading, setWgLoading] = useState(false);
  const [wgErr, setWgErr] = useState(null);
  // 22.7.92: a deliberate pause is not an error, so it gets amber not red.
  const [wgPaused, setWgPaused] = useState(false);
  const [wg, setWg] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  async function loadHistory() {
    try {
      const r = await fetch('/api/scenarios/wargames?limit=10');
      const d = await r.json();
      if (r.ok) setHistory(d.wargames || []);
    } catch { /* non-critical */ }
  }
  useEffect(() => { loadHistory(); }, []);

  async function wargame({ scenarioId = null, custom = null }) {
    setWgLoading(true);
    setWgErr(null);
    setWg(null);
    // 22.7.92 (codex R4-F3): reset with the rest, or a stale paused banner
    // describes the previous request instead of this one.
    setWgPaused(false);
    try {
      const r = await fetch('/api/scenarios/wargame', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenario_id: scenarioId, custom_scenario: custom, model: wgModel }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) { setWgErr(d.error || `HTTP ${r.status}`); return; }
      // Sprint 22.7.92: the paused skip is ok:true (so the route answers 200,
      // not 422), which means it clears the error branch above and carries no
      // .wargame payload - the spinner would just stop and render nothing, with
      // no reason given. Say why.
      if (d.skipped && d.reason === 'llm_paused') { setWgPaused(true); return; }
      setWgPaused(false);
      setWg(d);
      loadHistory();
    } catch (e) { setWgErr(e.message); }
    finally { setWgLoading(false); }
  }

  const CONF_COLOR = { high: '#10b981', medium: '#f59e0b', low: '#6b7280' };
  const EXP_COLOR = { benefits: '#10b981', hurt: '#ef4444', neutral: '#6b7280', unclear: '#f59e0b' };

  const pnl = result ? Number(result.totalPnlUsd) : 0;
  const pnlColor = pnl > 0 ? '#10b981' : pnl < 0 ? '#ef4444' : '#6b7280';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Map className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Scenario Engine</h1>
        <span className="text-korinth-muted text-sm">
          Market-shock impact on shadow positions, KDX, and AED purchasing power
        </span>
      </div>

      {err && <div className="text-xs text-red-400 font-mono">{err}</div>}

      {/* Scenario cards */}
      <ModuleContainer title="Scenarios" subtitle="Click a shock to simulate its impact on open shadow positions">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {scenarios.map((s) => (
            <button
              key={s.id}
              onClick={() => simulate(s.id)}
              className={`text-left p-3 rounded-lg border transition-colors ${
                selectedId === s.id
                  ? 'border-korinth-accent/60 bg-korinth-accent/10'
                  : 'border-korinth-border/30 bg-korinth-bg/40 hover:bg-korinth-bg/70'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-white">{s.name}</span>
                <Play className="w-3.5 h-3.5 text-korinth-muted shrink-0" />
              </div>
              <div className="text-[10px] font-mono text-korinth-muted mt-1">{changeLabel(s.changes)}</div>
            </button>
          ))}
          {scenarios.length === 0 && !err && (
            <div className="text-[11px] text-korinth-muted col-span-full">Loading scenarios…</div>
          )}
        </div>
      </ModuleContainer>

      {/* Result */}
      {(loading || result) && (
        <ModuleContainer
          title={result ? `Impact: ${result.scenario}` : 'Simulating…'}
          subtitle={result ? `${result.positionsAffected} of ${result.positionsTotal} open shadow positions affected` : ''}
        >
          {loading && <div className="text-[11px] text-korinth-muted">Running simulation…</div>}
          {result && (
            <div className="space-y-4">
              {/* KPI strip */}
              <div className="flex flex-wrap gap-4 text-[11px] font-mono">
                <div>
                  <div className="text-korinth-muted">Total shadow P&amp;L</div>
                  <div className="text-lg font-bold" style={{ color: pnlColor }}>
                    {pnl > 0 ? <TrendingUp className="w-4 h-4 inline -mt-0.5 mr-1" /> : pnl < 0 ? <TrendingDown className="w-4 h-4 inline -mt-0.5 mr-1" /> : null}
                    {fmtUsd(result.totalPnlUsd)} <span className="text-xs">({result.totalPnlPct}%)</span>
                  </div>
                </div>
                <div>
                  <div className="text-korinth-muted"><Landmark className="w-3 h-3 inline -mt-0.5 mr-1" />KDX impact</div>
                  <div className="text-lg font-bold text-korinth-text">{result.kdxImpact}%</div>
                </div>
                <div>
                  <div className="text-korinth-muted"><Globe className="w-3 h-3 inline -mt-0.5 mr-1" />AED purchasing power</div>
                  <div className="text-xs text-korinth-text mt-1">
                    EUR {result.aedPurchasingPower?.eur ?? 0}% · GBP {result.aedPurchasingPower?.gbp ?? 0}%
                  </div>
                </div>
              </div>

              {/* Position impact table */}
              {result.positionImpacts?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px] font-mono">
                    <thead>
                      <tr className="text-korinth-muted border-b border-korinth-border/30">
                        <th className="text-left py-1.5 px-2">Asset</th>
                        <th className="text-left py-1.5 px-2">Direction</th>
                        <th className="text-right py-1.5 px-2">Price move</th>
                        <th className="text-right py-1.5 px-2">Position impact</th>
                        <th className="text-right py-1.5 px-2">Shadow P&amp;L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.positionImpacts.map((p, i) => (
                        <tr key={i} className="border-b border-korinth-border/10 hover:bg-korinth-bg/50">
                          <td className="py-1.5 px-2 text-white">{p.asset}</td>
                          <td className="py-1.5 px-2 text-korinth-muted">{p.direction}</td>
                          <td className="py-1.5 px-2 text-right text-korinth-text">{p.changePct > 0 ? '+' : ''}{p.changePct}%</td>
                          <td className="py-1.5 px-2 text-right" style={{ color: p.impactPct >= 0 ? '#10b981' : '#ef4444' }}>{p.impactPct > 0 ? '+' : ''}{p.impactPct}%</td>
                          <td className="py-1.5 px-2 text-right font-bold" style={{ color: p.impactUsd >= 0 ? '#10b981' : '#ef4444' }}>{fmtUsd(p.impactUsd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-[11px] text-korinth-muted">No open shadow positions are affected by this scenario.</div>
              )}
              <div className="text-[10px] text-korinth-muted">{result.aedPurchasingPower?.note}</div>
            </div>
          )}
        </ModuleContainer>
      )}

      {/* ─── LLM War-game (22.7.33) ──────────────────────────────────── */}
      <ModuleContainer
        title="War-game (second-order)"
        subtitle="LLM consequence chains, exposure reads, hedges · on-demand, ~20-40s per run"
        icon={<Swords className="w-3.5 h-3.5" />}
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono text-korinth-muted">Model:</span>
            {['sonnet', 'fable'].map((m) => (
              <button
                key={m}
                onClick={() => setWgModel(m)}
                className={`text-[10px] font-mono px-2 py-0.5 rounded border ${wgModel === m ? 'border-korinth-accent/60 bg-korinth-accent/10 text-white' : 'border-korinth-border/30 text-korinth-muted hover:bg-korinth-bg/70'}`}
              >
                {m === 'sonnet' ? 'Sonnet 4.6 (default)' : 'Fable 5 (A/B eval)'}
              </button>
            ))}
            {selectedId && (
              <button
                onClick={() => wargame({ scenarioId: selectedId })}
                disabled={wgLoading}
                className="text-[11px] font-mono px-3 py-1 rounded border border-korinth-accent/60 bg-korinth-accent/10 text-white hover:bg-korinth-accent/20 disabled:opacity-50"
              >
                <Swords className="w-3 h-3 inline -mt-0.5 mr-1" />War-game selected scenario
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              value={wgCustom}
              onChange={(e) => setWgCustom(e.target.value)}
              maxLength={500}
              placeholder="Or describe a custom scenario, e.g. 'Strait of Hormuz closed for 2 weeks'"
              className="flex-1 text-[11px] font-mono bg-korinth-bg/60 border border-korinth-border/30 rounded px-2 py-1.5 text-korinth-text placeholder-korinth-muted focus:outline-none focus:border-korinth-accent/50"
            />
            <button
              onClick={() => wgCustom.trim() && wargame({ custom: wgCustom.trim() })}
              disabled={wgLoading || !wgCustom.trim()}
              className="text-[11px] font-mono px-3 py-1.5 rounded border border-korinth-border/40 text-korinth-text hover:bg-korinth-bg/70 disabled:opacity-50"
            >
              Run
            </button>
          </div>

          {wgLoading && <div className="text-[11px] font-mono text-korinth-muted animate-pulse">War-gaming second-order consequences… (~20-40s, one LLM call)</div>}
          {wgErr && <div className="text-[11px] font-mono text-red-400">Error: {wgErr}</div>}
          {wgPaused && (
            <div className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] font-mono text-amber-400">
              Paused - LLM lanes are switched off (KORINTH_PAUSE_LLM). No war-game was run; earlier runs are still listed below.
            </div>
          )}

          {wg?.wargame && (
            <div className="space-y-3 rounded-lg border border-korinth-border/30 bg-korinth-bg/40 p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-sm font-bold text-white">{wg.scenario}</span>
                <span className="text-[10px] font-mono text-korinth-muted">
                  P(scenario) {wg.wargame.probability_pct}% · {wg.model} · {Math.round(wg.durationMs / 1000)}s
                </span>
              </div>

              {Array.isArray(wg.wargame.second_order_chain) && (
                <div>
                  <div className="text-[10px] font-mono uppercase text-korinth-muted mb-1">Second-order chain</div>
                  <ol className="space-y-1.5">
                    {wg.wargame.second_order_chain.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-[11px]">
                        <span className="font-mono font-bold text-korinth-accent shrink-0">{s.step}.</span>
                        <div>
                          <span className="text-korinth-text">{s.effect}</span>
                          <div className="text-[9.5px] font-mono text-korinth-muted">
                            {(s.affected_assets || []).join(', ')} · {s.direction} {s.magnitude_pct_range} · {s.timeframe} · <span style={{ color: CONF_COLOR[s.confidence] }}>{s.confidence}</span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {Array.isArray(wg.wargame.portfolio_exposure_read) && wg.wargame.portfolio_exposure_read.length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase text-korinth-muted mb-1">Your exposure</div>
                  <div className="flex flex-wrap gap-1.5">
                    {wg.wargame.portfolio_exposure_read.map((p, i) => (
                      <span key={i} title={p.why} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-korinth-border/30 bg-korinth-bg/60 cursor-help">
                        {p.symbol} <span style={{ color: EXP_COLOR[p.exposure] }} className="font-bold">{p.exposure}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(wg.wargame.hedges) && wg.wargame.hedges.length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase text-korinth-muted mb-1">Hedge candidates</div>
                  <ul className="space-y-1">
                    {wg.wargame.hedges.map((h, i) => (
                      <li key={i} className="text-[11px]">
                        <span className="text-white font-semibold">{h.action}</span> <span className="text-korinth-muted">via</span> <span className="text-korinth-text">{h.instrument}</span>
                        <div className="text-[9.5px] text-korinth-muted">{h.rationale} · cost: {h.cost_consideration}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Array.isArray(wg.wargame.invalidation_signals) && wg.wargame.invalidation_signals.length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase text-korinth-muted mb-1">Invalidation signals</div>
                    <ul className="list-disc list-inside text-[10.5px] text-korinth-text space-y-0.5">
                      {wg.wargame.invalidation_signals.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {Array.isArray(wg.wargame.historical_analogs) && wg.wargame.historical_analogs.length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase text-korinth-muted mb-1">Historical analogs</div>
                    <ul className="space-y-1 text-[10.5px]">
                      {wg.wargame.historical_analogs.map((a, i) => (
                        <li key={i}><span className="text-white">{a.event}:</span> <span className="text-korinth-text">{a.what_happened}</span> <span className="text-korinth-muted">({a.relevance})</span></li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="text-[11px] text-korinth-text border-t border-korinth-border/30 pt-2">
                <span className="text-[10px] font-mono uppercase text-korinth-muted mr-2">Net assessment</span>
                {wg.wargame.net_assessment}
              </div>
            </div>
          )}

          {history.length > 0 && (
            <div>
              <button onClick={() => setShowHistory(!showHistory)} className="text-[10px] font-mono text-korinth-accent inline-flex items-center gap-1">
                <ChevronDown className={`w-3 h-3 transition-transform ${showHistory ? 'rotate-180' : ''}`} /> Past war-games ({history.length})
              </button>
              {showHistory && (
                <div className="mt-1.5 space-y-1">
                  {history.map((h) => (
                    <div key={h.id} className="flex items-center justify-between gap-2 text-[10.5px] font-mono rounded border border-korinth-border/20 px-2 py-1">
                      <span className="text-korinth-text truncate">{h.name}</span>
                      <span className="text-korinth-muted shrink-0">P {h.probability ?? '-'}% · {h.structural_validity?.model_key || '-'} · {String(h.created_at).slice(0, 16)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </ModuleContainer>
    </div>
  );
}
